import React, { useState, useEffect, useRef, useCallback } from 'react';

import {
  TeacherDashboard,
  AdminDashboard,
  RoleBasedQuickActions,
} from '../../components/dashboard/RoleBasedDashboard';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useDashboardMetrics } from '../../hooks/useAnalytics';
import { usePermissions } from '../../hooks/usePermissions';
import {
  announceToScreenReader,
  ariaUtils,
  keyboardNavigation,
} from '../../utils/accessibility';

export const DashboardPage: React.FC = () => {
  const { user, isTeacher, isSchoolAdmin, isDistrictAdmin } = usePermissions();
  const { data, loading, error } = useDashboardMetrics();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const mainContentRef = useRef<HTMLElement>(null);
  const skipLinkRef = useRef<HTMLAnchorElement>(null);

  // Generate unique IDs for ARIA relationships
  const pageHeadingId = ariaUtils.generateId('dashboard-heading');
  const mainContentId = ariaUtils.generateId('main-content');
  const quickActionsId = ariaUtils.generateId('quick-actions');

  // Get user's display name - memoized with useCallback
  const getUserDisplayName = useCallback(() => {
    if (user?.full_name) {
      return user.full_name;
    }
    return null;
  }, [user?.full_name]);

  // Announce page load to screen readers
  useEffect(() => {
    if (isInitialLoad && !loading) {
      const roleText = isTeacher
        ? 'teacher'
        : isSchoolAdmin
          ? 'school administrator'
          : isDistrictAdmin
            ? 'district administrator'
            : 'user';

      const userName = getUserDisplayName();

      announceToScreenReader(
        `Dashboard loaded. Welcome ${userName || roleText}. ${
          error
            ? 'There was an error loading some data.'
            : 'All data loaded successfully.'
        }`,
        'polite'
      );
      setIsInitialLoad(false);
    }
  }, [
    isInitialLoad,
    loading,
    error,
    isTeacher,
    isSchoolAdmin,
    isDistrictAdmin,
    getUserDisplayName,
  ]);

  // Handle skip navigation
  const handleSkipToMain = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    mainContentRef.current?.focus();
    announceToScreenReader('Skipped to main content', 'polite');
  };

  // Handle keyboard navigation for skip link
  const handleSkipLinkKeyDown = (event: React.KeyboardEvent) => {
    keyboardNavigation.handleActivation(event.nativeEvent, () => {
      handleSkipToMain(event);
    });
  };

  // Determine role-specific welcome message and page title
  const getWelcomeMessage = () => {
    if (isTeacher) {
      return {
        title: 'Teacher Dashboard',
        subtitle: 'Manage your evaluations and track your professional growth',
        ariaLabel:
          'Teacher dashboard with evaluation metrics and recent activities',
      };
    } else if (isSchoolAdmin) {
      return {
        title: 'School Administrator Dashboard',
        subtitle: 'Oversee school operations and teacher evaluations',
        ariaLabel:
          'School administrator dashboard with school-wide metrics and activities',
      };
    } else if (isDistrictAdmin) {
      return {
        title: 'District Administrator Dashboard',
        subtitle: 'Monitor district-wide performance and manage schools',
        ariaLabel:
          'District administrator dashboard with district-wide metrics and activities',
      };
    } else {
      return {
        title: 'Dashboard',
        subtitle: 'Welcome to the Teacher Evaluation System',
        ariaLabel: 'General dashboard view',
      };
    }
  };

  const { title, subtitle, ariaLabel } = getWelcomeMessage();

  // Handle error state with accessibility
  if (error) {
    return (
      <DashboardLayout
        title="Dashboard Error"
        subtitle="Unable to load dashboard data"
      >
        <div
          className="min-h-screen flex items-center justify-center"
          role="alert"
          aria-live="assertive"
        >
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-6xl mb-4" aria-hidden="true">
              ⚠️
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Dashboard Error
            </h1>
            <p className="text-gray-600 mb-4">
              We're having trouble loading your dashboard. Please try refreshing
              the page or contact support if the problem persists.
            </p>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={() => window.location.reload()}
              aria-describedby="refresh-help"
            >
              Refresh Page
            </button>
            <span id="refresh-help" className="sr-only">
              Reload the dashboard to try again
            </span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const userName = getUserDisplayName();

  return (
    <DashboardLayout title={title} subtitle={subtitle}>
      {/* Skip Navigation Link */}
      <a
        ref={skipLinkRef}
        href={`#${mainContentId}`}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-md z-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        onClick={handleSkipToMain}
        onKeyDown={handleSkipLinkKeyDown}
      >
        Skip to main content
      </a>

      {/* Page Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                id={pageHeadingId}
                className="text-2xl font-bold text-gray-900"
              >
                {title}
                {userName && (
                  <span className="text-lg font-normal text-gray-600 ml-2">
                    Welcome back, {userName}
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-1">{subtitle}</p>
            </div>

            {/* Loading indicator */}
            {loading && (
              <div
                className="flex items-center space-x-2"
                role="status"
                aria-live="polite"
                aria-label="Loading dashboard data"
              >
                <div
                  className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"
                  aria-hidden="true"
                ></div>
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        ref={mainContentRef}
        id={mainContentId}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8"
        role="main"
        aria-labelledby={pageHeadingId}
        aria-describedby={quickActionsId}
        tabIndex={-1}
      >
        <div className="space-y-8">
          {/* Quick Actions Section */}
          <section id={quickActionsId} aria-label="Quick actions and shortcuts">
            <RoleBasedQuickActions />
          </section>

          {/* Role-specific Dashboard Content */}
          <section aria-label={ariaLabel} className="focus:outline-none">
            {loading ? (
              <div
                className="flex items-center justify-center py-12"
                role="status"
                aria-live="polite"
              >
                <div className="text-center">
                  <div
                    className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"
                    aria-hidden="true"
                  ></div>
                  <p className="text-gray-600">Loading your dashboard...</p>
                </div>
              </div>
            ) : isTeacher ? (
              <TeacherDashboard />
            ) : (isSchoolAdmin || isDistrictAdmin) && data ? (
              <AdminDashboard data={data} />
            ) : (
              <div
                className="text-center py-12"
                role="region"
                aria-label="Default dashboard view"
              >
                <div className="max-w-md mx-auto">
                  <div className="text-6xl mb-4" aria-hidden="true">
                    👋
                  </div>
                  <h2 className="text-xl font-medium text-gray-900 mb-2">
                    Welcome to the Teacher Evaluation System
                  </h2>
                  <p className="text-gray-600">
                    Your role-specific dashboard will appear here once your
                    account permissions are configured.
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* Development Debug Info (only in development) */}
          {process.env.NODE_ENV === 'development' && (
            <section
              className="mt-8 p-4 bg-gray-100 border border-gray-300 rounded-lg"
              aria-labelledby="debug-heading"
            >
              <h3
                id="debug-heading"
                className="text-sm font-medium text-gray-700 mb-2"
              >
                Development Debug Info
                <span className="sr-only">
                  (visible only in development mode)
                </span>
              </h3>
              <div className="text-xs text-gray-600 space-y-1">
                <p>
                  <strong>User Role:</strong> {user?.role || 'No role assigned'}
                </p>
                <p>
                  <strong>Permissions:</strong> Teacher:{' '}
                  {isTeacher ? 'Yes' : 'No'}, School Admin:{' '}
                  {isSchoolAdmin ? 'Yes' : 'No'}, District Admin:{' '}
                  {isDistrictAdmin ? 'Yes' : 'No'}
                </p>
                <p>
                  <strong>Data Status:</strong> Loading:{' '}
                  {loading ? 'Yes' : 'No'}, Error: {error ? 'Yes' : 'No'}
                </p>
              </div>
            </section>
          )}
        </div>
      </main>
    </DashboardLayout>
  );
};
